import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';
import apiClient from '../../utils/api';

const MembersContainer = styled.div`
  max-width: 1200px;
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    align-items: stretch;
  }
`;

const Title = styled.h2`
  font-size: 1.75rem;
  font-weight: 700;
  color: #000000;
`;

const SearchSection = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    padding: 1.25rem;
    border-radius: 12px;
  }
`;

const SearchRow = styled.div`
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 1rem;
  align-items: flex-end;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FilterRow = styled.div`
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
  flex-wrap: wrap;
  justify-content: space-between;
`;

const FilterButton = styled.button<{ active?: boolean }>`
  padding: 0.5rem 0.875rem;
  border-radius: 8px;
  border: 2px solid ${props => props.active ? '#000000' : '#dee2e6'};
  background: ${props => props.active ? '#000000' : 'white'};
  color: ${props => props.active ? 'white' : '#6c757d'};
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  
  &:hover {
    border-color: #000000;
    background: ${props => props.active ? '#000000' : '#f8f9fa'};
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 0.75rem;
    font-size: 0.8125rem;
  }
`;

const StatsRow = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 2rem;
  justify-content: space-between;
  
  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`;

const StatCard = styled.div`
  background: white;
  padding: 0.875rem 1rem;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  text-align: center;
  flex: 1;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.75rem;
  color: #6c757d;
  margin-bottom: 0.375rem;
  font-weight: 600;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    font-size: 0.6875rem;
  }
`;

const StatValue = styled.div`
  font-size: 1.375rem;
  font-weight: 700;
  color: #000000;
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
  }
`;

const MembersList = styled.div`
  background: white;
  padding: 2rem;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  
  @media (max-width: 768px) {
    padding: 1rem;
    border-radius: 12px;
  }
`;

const TableContainer = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
`;

const Thead = styled.thead`
  background: #f8f9fa;
  border-bottom: 2px solid #dee2e6;
`;

const Th = styled.th`
  padding: 1rem 0.75rem;
  text-align: left;
  font-size: 0.875rem;
  font-weight: 700;
  color: #495057;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.25rem;
    font-size: 0.75rem;
  }
`;

const Tbody = styled.tbody``;

const Tr = styled.tr`
  border-bottom: 1px solid #dee2e6;
  transition: background 0.2s ease;
  cursor: pointer;
  
  &:hover {
    background: #f8f9fa;
  }
  
  &:last-child {
    border-bottom: none;
  }
`;

const Td = styled.td`
  padding: 1rem 0.75rem;
  font-size: 0.875rem;
  color: #495057;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.25rem;
    font-size: 0.75rem;
  }
`;

const NameCell = styled.td`
  padding: 1rem 0.75rem;
  font-size: 0.875rem;
  font-weight: 700;
  color: #000000;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.25rem;
    font-size: 0.75rem;
  }
`;

const NameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    gap: 0.25rem;
  }
`;

const NameText = styled.span`
  white-space: nowrap;
`;

const RoleBadge = styled.span<{ role?: string }>`
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 600;
  background: ${props => {
    if (props.role === '회장') return '#e7f5ff';
    if (props.role === '부회장') return '#fff3e0';
    if (props.role === '총무') return '#f3e5f5';
    if (props.role === '매니저') return '#e8f5e9';
    return '#f8f9fa';
  }};
  color: ${props => {
    if (props.role === '회장') return '#1971c2';
    if (props.role === '부회장') return '#e67700';
    if (props.role === '총무') return '#9c27b0';
    if (props.role === '매니저') return '#2e7d32';
    return '#495057';
  }};
  white-space: nowrap;
  display: inline-block;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    padding: 0.2rem 0.4rem;
    font-size: 0.65rem;
  }
`;

const StatusBadge = styled.span<{ status?: string }>`
  padding: 0.25rem 0.5rem;
  border-radius: 6px;
  font-size: 0.7rem;
  font-weight: 600;
  background: ${props => {
    if (props.status === '활동') return '#d1e7dd';
    if (props.status === '휴면') return '#fff3cd';
    return '#f8d7da';
  }};
  color: ${props => {
    if (props.status === '활동') return '#0f5132';
    if (props.status === '휴면') return '#856404';
    return '#842029';
  }};
  white-space: nowrap;
  display: inline-block;
  
  @media (max-width: 768px) {
    padding: 0.2rem 0.4rem;
    font-size: 0.65rem;
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem 1rem;
  color: #adb5bd;
  font-size: 1rem;
`;

const ModalOverlay = styled.div<{ isOpen: boolean }>`
  display: ${props => props.isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 9999;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  
  @media (max-width: 768px) {
    align-items: flex-end;
    padding: 0;
  }
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 24px;
  padding: 2rem;
  max-width: 500px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    border-radius: 24px 24px 0 0;
    max-height: 80vh;
    padding: 1.5rem;
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1.5rem;
`;

const ModalTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  color: #000000;
  margin: 0;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0.25rem;
  
  &:hover {
    color: #000000;
  }
`;

const ModalForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const RadioLabel = styled.label`
  font-size: 0.875rem;
  font-weight: 600;
  color: #343a40;
  margin-bottom: 0.5rem;
  display: block;
`;

const RadioOptions = styled.div`
  display: flex;
  gap: 1.5rem;
`;

const RadioOption = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;
  font-size: 0.9375rem;
  color: #343a40;
`;

const RadioInput = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
  accent-color: #000000;
`;

const ModalActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-top: 1.5rem;
  
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

interface Member {
  id: number;
  user_id?: string;  // 아이디 추가
  name: string;
  phone: string;
  birthYear: string;
  address: string;
  gender: string;
  electionStatus: string;
  status: string;
  registrationDate: string;
  dormantStatus: string;
}

const AdminMembers: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>('전체');
  const [filterGender, setFilterGender] = useState<string>('전체');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    birthYear: '',
    address: '',
    gender: '',
    electionStatus: '',
    status: '',
    dormantStatus: 'n',
    registrationDate: ''
  });

  // 생년월일 포맷 함수 (YYYYMMDD -> YY년생)
  const formatBirthYear = (birthYear: string): string => {
    if (birthYear.length >= 4) {
      return `${birthYear.slice(0, 2)}년생`;
    }
    return birthYear;
  };

  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState(true);

  // 회원 목록 조회
  const fetchMembers = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get('/members/active');
      if (response.data.success) {
        setMembers(response.data.data);
      }
    } catch (error) {
      console.error('회원 목록 조회 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMembers();
  }, []);

  // 관리자 직급 우선순위
  const getAdminPriority = (role: string): number => {
    const priorityMap: { [key: string]: number } = {
      '회장': 1,
      '부회장': 2,
      '총무': 3,
      '매니저': 4,
      '여자코치': 5
    };
    return priorityMap[role] || 999; // 관리자가 아니면 999
  };

  // 필터링된 회원 목록
  const filteredMembers = members
    .filter(member => {
      const matchesSearch = member.name.includes(searchTerm);
      const matchesStatus = filterStatus === '전체' || 
                           (filterStatus === '활동' && member.dormantStatus === 'n') ||
                           (filterStatus === '휴면' && member.dormantStatus === 'y');
      const matchesGender = filterGender === '전체' || member.gender === filterGender;
      
      return matchesSearch && matchesStatus && matchesGender;
    })
    .sort((a, b) => {
      // 관리자 우선순위로 정렬
      const aPriority = getAdminPriority(a.status);
      const bPriority = getAdminPriority(b.status);
      
      // 관리자가 아닌 경우 (999)는 일반 회원으로 취급
      const aIsAdmin = aPriority < 999;
      const bIsAdmin = bPriority < 999;
      
      // 관리자와 일반 회원 분리: 관리자 먼저
      if (aIsAdmin && !bIsAdmin) return -1;
      if (!aIsAdmin && bIsAdmin) return 1;
      
      // 둘 다 관리자인 경우: 우선순위로 정렬
      if (aIsAdmin && bIsAdmin) {
        return aPriority - bPriority;
      }
      
      // 둘 다 일반 회원인 경우: 이름순 정렬
      return a.name.localeCompare(b.name, 'ko');
    });

  // 통계
  const stats = {
    total: members.length,
    active: members.filter(m => m.dormantStatus === 'n').length,
    dormant: members.filter(m => m.dormantStatus === 'y').length,
    male: members.filter(m => m.gender === '남자').length,
    female: members.filter(m => m.gender === '여자').length,
  };

  const handleMemberClick = (member: Member) => {
    setEditingMember(member);
    setFormData({
      name: member.name,
      phone: member.phone,
      birthYear: member.birthYear,
      address: member.address,
      gender: member.gender,
      electionStatus: member.electionStatus,
      status: member.status,
      dormantStatus: member.dormantStatus,
      registrationDate: member.registrationDate || ''
    });
    setIsModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!editingMember) return;
    
    try {
      const response = await apiClient.put(`/members/${editingMember.id}/info`, {
        name: formData.name,
        phone: formData.phone,
        birth_date: formData.birthYear,
        address: formData.address,
        gender: formData.gender,
        is_elected: formData.electionStatus === '선출' ? 'Y' : 'N',
        status: formData.status,
        dormant_status: formData.dormantStatus,
        registered_date: formData.registrationDate
      });
      
      if (response.data.success) {
        window.alert('회원 정보가 수정되었습니다');
        fetchMembers(); // 목록 새로고침
        setIsModalOpen(false);
        setEditingMember(null);
      }
    } catch (error: any) {
      console.error('회원 수정 실패:', error);
      window.alert(error.response?.data?.detail || '회원 정보 수정에 실패했습니다');
    }
  };

  const handleDelete = async () => {
    if (!editingMember) return;
    
    if (!window.confirm('정말 이 회원을 삭제하시겠습니까?')) return;
    
    try {
      const response = await apiClient.delete(`/members/${editingMember.id}/delete`);
      
      if (response.data.success) {
        window.alert('회원이 삭제되었습니다');
        fetchMembers(); // 목록 새로고침
        setIsModalOpen(false);
        setEditingMember(null);
      }
    } catch (error: any) {
      console.error('회원 삭제 실패:', error);
      window.alert(error.response?.data?.detail || '회원 삭제에 실패했습니다');
    }
  };

  const formatPhoneNumber = (value: string) => {
    const numbers = value.replace(/[^\d]/g, '');
    const limited = numbers.slice(0, 11);
    
    if (limited.length <= 3) {
      return limited;
    } else if (limited.length <= 7) {
      return `${limited.slice(0, 3)}-${limited.slice(3)}`;
    } else {
      return `${limited.slice(0, 3)}-${limited.slice(3, 7)}-${limited.slice(7)}`;
    }
  };

  const handleInputChange = (field: string, value: string) => {
    let formattedValue = value;
    
    if (field === 'phone') {
      formattedValue = formatPhoneNumber(value);
    }
    
    if (field === 'birthYear') {
      const numbers = value.replace(/[^\d]/g, '');
      formattedValue = numbers.slice(0, 6);
    }
    
    setFormData(prev => ({
      ...prev,
      [field]: formattedValue
    }));
  };

  return (
    <DashboardLayout title="회원 명부">
      <MembersContainer>
        <Header>
          <Title>회원 명부</Title>
        </Header>
        
        <StatsRow>
          <StatCard>
            <StatLabel>전체</StatLabel>
            <StatValue>{stats.total}명</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>활동</StatLabel>
            <StatValue>{stats.active}명</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>휴면</StatLabel>
            <StatValue>{stats.dormant}명</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>남자</StatLabel>
            <StatValue>{stats.male}명</StatValue>
          </StatCard>
          <StatCard>
            <StatLabel>여자</StatLabel>
            <StatValue>{stats.female}명</StatValue>
          </StatCard>
        </StatsRow>
        
        <SearchSection>
          <SearchRow>
            <Input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="이름으로 검색"
            />
            <Button 
              variant="primary"
              onClick={() => {}}
            >
              검색
            </Button>
          </SearchRow>
          
          <FilterRow>
            <FilterButton 
              active={filterStatus === '전체'}
              onClick={() => setFilterStatus('전체')}
            >
              전체
            </FilterButton>
            <FilterButton 
              active={filterStatus === '활동'}
              onClick={() => setFilterStatus('활동')}
            >
              활동
            </FilterButton>
            <FilterButton 
              active={filterStatus === '휴면'}
              onClick={() => setFilterStatus('휴면')}
            >
              휴면
            </FilterButton>
            <FilterButton 
              active={filterGender === '남자'}
              onClick={() => setFilterGender(filterGender === '남자' ? '전체' : '남자')}
            >
              남자
            </FilterButton>
            <FilterButton 
              active={filterGender === '여자'}
              onClick={() => setFilterGender(filterGender === '여자' ? '전체' : '여자')}
            >
              여자
            </FilterButton>
          </FilterRow>
        </SearchSection>
        
        <MembersList>
          {loading ? (
            <EmptyState>로딩 중...</EmptyState>
          ) : filteredMembers.length > 0 ? (
            <TableContainer>
              <Table>
                <Thead>
                  <tr>
                    <Th>이름</Th>
                    <Th>아이디</Th>
                    <Th>생년월일</Th>
                    <Th>성별</Th>
                    <Th>선출</Th>
                    <Th>활동</Th>
                  </tr>
                </Thead>
                <Tbody>
                  {filteredMembers.map(member => (
                    <Tr key={member.id} onClick={() => handleMemberClick(member)}>
                      <NameCell>
                        <NameContainer>
                          <NameText>{member.name}</NameText>
                          <RoleBadge role={member.status}>
                            {member.status}
                          </RoleBadge>
                        </NameContainer>
                      </NameCell>
                      <Td>{member.user_id || '-'}</Td>
                      <Td>{formatBirthYear(member.birthYear)}</Td>
                      <Td>{member.gender}</Td>
                      <Td>{member.electionStatus}</Td>
                      <Td>
                        <StatusBadge status={member.dormantStatus === 'n' ? '활동' : '휴면'}>
                          {member.dormantStatus === 'n' ? '활동' : '휴면'}
                        </StatusBadge>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          ) : (
            <EmptyState>검색 결과가 없습니다.</EmptyState>
          )}
        </MembersList>
        
        {/* 회원 정보 수정 모달 */}
        <ModalOverlay 
          isOpen={isModalOpen}
          onClick={() => setIsModalOpen(false)}
        >
          <ModalContent onClick={(e) => e.stopPropagation()}>
            <ModalHeader>
              <ModalTitle>회원 정보 수정</ModalTitle>
              <CloseButton onClick={() => setIsModalOpen(false)}>×</CloseButton>
            </ModalHeader>
            
            <ModalForm onSubmit={handleSubmit}>
              <Input
                label="이름"
                type="text"
                value={formData.name}
                onChange={(e) => handleInputChange('name', e.target.value)}
                required
              />
              
              <Input
                label="전화번호"
                type="tel"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                placeholder="010-0000-0000"
                required
              />
              
              <Input
                label="생년월일"
                type="text"
                value={formData.birthYear}
                onChange={(e) => handleInputChange('birthYear', e.target.value)}
                placeholder="6자리 (예: 231105)"
                required
              />
              
              <Input
                label="가입일"
                type="date"
                value={formData.registrationDate}
                onChange={(e) => handleInputChange('registrationDate', e.target.value)}
              />
              
              <Input
                label="주소"
                type="text"
                value={formData.address}
                onChange={(e) => handleInputChange('address', e.target.value)}
                required
              />
              
              <RadioGroup>
                <RadioLabel>성별</RadioLabel>
                <RadioOptions>
                  <RadioOption>
                    <RadioInput
                      type="radio"
                      name="gender"
                      value="남자"
                      checked={formData.gender === '남자'}
                      onChange={(e) => handleInputChange('gender', e.target.value)}
                    />
                    남자
                  </RadioOption>
                  <RadioOption>
                    <RadioInput
                      type="radio"
                      name="gender"
                      value="여자"
                      checked={formData.gender === '여자'}
                      onChange={(e) => handleInputChange('gender', e.target.value)}
                    />
                    여자
                  </RadioOption>
                </RadioOptions>
              </RadioGroup>
              
              <RadioGroup>
                <RadioLabel>선출 여부</RadioLabel>
                <RadioOptions>
                  <RadioOption>
                    <RadioInput
                      type="radio"
                      name="electionStatus"
                      value="선출"
                      checked={formData.electionStatus === '선출'}
                      onChange={(e) => handleInputChange('electionStatus', e.target.value)}
                    />
                    선출
                  </RadioOption>
                  <RadioOption>
                    <RadioInput
                      type="radio"
                      name="electionStatus"
                      value="비선출"
                      checked={formData.electionStatus === '비선출'}
                      onChange={(e) => handleInputChange('electionStatus', e.target.value)}
                    />
                    비선출
                  </RadioOption>
                </RadioOptions>
              </RadioGroup>
              
              <Input
                label="회원 상태/직책"
                type="text"
                value={formData.status}
                onChange={(e) => handleInputChange('status', e.target.value)}
                placeholder="예: 회원, 회장, 부회장 등"
                required
              />
              
              <RadioGroup>
                <RadioLabel>휴면 상태</RadioLabel>
                <RadioOptions>
                  <RadioOption>
                    <RadioInput
                      type="radio"
                      name="dormantStatus"
                      value="n"
                      checked={formData.dormantStatus === 'n'}
                      onChange={(e) => handleInputChange('dormantStatus', e.target.value)}
                    />
                    정상
                  </RadioOption>
                  <RadioOption>
                    <RadioInput
                      type="radio"
                      name="dormantStatus"
                      value="y"
                      checked={formData.dormantStatus === 'y'}
                      onChange={(e) => handleInputChange('dormantStatus', e.target.value)}
                    />
                    휴면
                  </RadioOption>
                </RadioOptions>
              </RadioGroup>
              
              <ModalActions>
                <Button
                  type="button"
                  variant="danger"
                  size="large"
                  fullWidth
                  onClick={handleDelete}
                >
                  삭제
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="large"
                  fullWidth
                >
                  수정
                </Button>
              </ModalActions>
            </ModalForm>
          </ModalContent>
        </ModalOverlay>
      </MembersContainer>
    </DashboardLayout>
  );
};

export default AdminMembers;

